import React, { useState } from 'react'
import { useDefaultValueTriggerUpdate, useRemoveMandatoryError } from "../Utils/FormBuilderUtils";
import { Controller } from "react-hook-form";
import { formBuilderParams } from "../FormBuilder";
import { formRulesBuilder } from "../lib/FormBuilderRules";
import { Backdrop, IconButton, Paper, Stack, Typography } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import Label from "components/Input/Label/Label";
import { Description, InsertDriveFile, Link } from "@mui/icons-material";
import useColors from "utils/useColors/useColors";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const PAGE_LOCALE_KEY = "component.form.filePicker.";

/** @type {Record<string, string>} */
const FILE_TYPE_MAP = {
  "application/pdf": "PDF",
}

/**
 * @typedef {"pdf" | "httpLink" | "any"} fileType
 * 
 * @typedef {Object} formBuilderFilePickerFileFormValueType
 * @property {import("../Component/FormBuilderFilePicker").fileType} type - File type
 * @property {string} [name] - Shown name of the file, can be empty if content is a http link
 * @property {string} content - Base64 string, http link
 */

/**
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseFormComponentProps} baseFormComponentProps
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseConfig} baseConfig
 * @typedef {import("../lib/FormBuilderFieldBuilder").filePickerConfig} filePickerConfig
 * @param {baseFormComponentProps & baseConfig & filePickerConfig} props
 */
const FormBuilderFilePicker = ({
  formProps,
  t,
  fieldName,
  mandatory = false,
  label,
  subLabel,
  hideLabel,
  noErrorMessage = false,
  allowPreview,
  allowMultiple,
  defaultFiles,
  allowedMimeTypes,
  maxSize,
  placeholder,
  customOnChangeFn,
  disabled = false,
  readOnly = false,
}) => {
  useRemoveMandatoryError({ fieldName, mandatory, formProps });
  useDefaultValueTriggerUpdate({ fieldName, defaultValue: defaultFiles, formProps }); //Always trigger
  const c = useColors();
  const inputRef = React.useRef(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const openPreview = (index) => {
    setPreviewOpen(true);
    setPreviewIndex(index);
  }
  /** @param {formBuilderFilePickerFileFormValueType[]} newValue */
  const onFileChange = (newValue, onChange) => {
    onChange(newValue);
    customOnChangeFn && customOnChangeFn(newValue, formProps);
  }

  const handleFileChange = (e, currentValue, onChange, onError) => {
    const INVALID_FILE_TYPE_MESSAGE = t(PAGE_LOCALE_KEY + "error.invalidFileType", { type: allowedMimeTypes.map((type) => FILE_TYPE_MAP[type]).join("/") });

    /** @type {File} */
    const file = e.target.files?.[0];

    const readFile = () => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64file = reader.result;
        const signature = reader?.result?.split(",")[1];
        /** @type {fileType?} */
        let fileType = null;
        allowedMimeTypes.forEach((type) => {
          switch (type) {
            case "application/pdf":
              if (signature?.startsWith("JVBERi0")) {
                fileType = "pdf"
              }
              break;
          }
        })
        if (fileType) {
          onFileChange([...currentValue, { type: fileType, name: file.name, content: base64file }], onChange);
        } else {
          onError(INVALID_FILE_TYPE_MESSAGE);
        }
      }

      reader.readAsDataURL(file);
    }

    if (!file) {
      return;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      onError(INVALID_FILE_TYPE_MESSAGE);
      return;
    }

    if (file.size > maxSize) {
      onError(t(PAGE_LOCALE_KEY + "error.invalidFileSize", { size: maxSize / 1024 / 1024 }));
      return;
    }

    readFile();
  }

  return (
    <Controller
      key={fieldName}
      name={fieldName}
      control={formProps.control}
      defaultValue={defaultFiles}
      rules={formRulesBuilder({ mandatory })}
      render={
        /**
          * @param {{
          *   field: {
          *     value: formBuilderFilePickerFileFormValueType[],
          *     onChange: (value: formBuilderFilePickerFileFormValueType[]) => void
          *   },
          *   fieldState: {
          *     error: ?Object
          *   }
          * }} props - Props from React Hook Form's Controller render.
          */
        ({ field: { value, onChange }, fieldState: { error } }) => {
          const canPreview = allowPreview && value?.length > 0;
          const canSelect = allowMultiple || !value?.length;

          return (
            <>
              <input
                type="file"
                accept={allowedMimeTypes?.join(", ")}
                value={""} //force uncontrolled to prevent bug
                onChange={(e) => handleFileChange(e, value ?? [], onChange, (errorMessage) => formProps.setError(fieldName, { type: "manual", message: errorMessage }))}
                disabled={disabled || readOnly}
                style={{ display: "none" }}
                ref={inputRef}
              />
              <Stack direction="column" gap={0.5} sx={{ ...formBuilderParams.formItemStyle }}>
                {hideLabel !== "removePadding" && <Label label={label} subLabel={subLabel} mandatory={mandatory} empty={hideLabel === "keepPadding"} />}
                {!readOnly && canSelect && <ButtonMAS
                  onClick={() => inputRef.current.click()}
                  disabled={disabled}
                  style={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(PAGE_LOCALE_KEY + "button.upload")}
                </ButtonMAS>}
                {
                  canSelect && readOnly && (
                    <Typography variant="content">
                      {placeholder ?? (t(PAGE_LOCALE_KEY + "placeholder.readOnly"))}
                    </Typography>
                    // readOnly ?
                    //   <Typography variant="content">
                    //     {placeholder ?? (t(PAGE_LOCALE_KEY + "placeholder.readOnly"))}
                    //   </Typography>
                    //   :
                    //   <Typography variant="content">
                    //     {placeholder ?? (allowMultiple ? t(PAGE_LOCALE_KEY + "placeholder.multiple") : t(PAGE_LOCALE_KEY + "placeholder.single"))}
                    //   </Typography>
                  )
                }
                {
                  value?.length > 0 && value.map((fileDetail, index) => {
                    const previewAvailable = canPreview && fileDetail.type !== "any";
                    const getDefaultname = () => {
                      switch (fileDetail.type) {
                        case "pdf":
                          return "Pdf file";
                        case "httpLink":
                          return fileDetail.content; //http link
                        default:
                          return "File";
                      }
                    };

                    return (
                      <Stack key={index} direction="row" alignItems="center" gap={1}>
                        {(() => {
                          switch (fileDetail.type) {
                            case "pdf":
                              return <Description
                                sx={{ fontSize: "18px", color: "#FA0F00" }}
                              />
                            case "httpLink":
                              return <Link
                                sx={{ fontSize: "18px", color: "#666" }}
                              />
                            default:
                              return <InsertDriveFile
                                sx={{ fontSize: "18px", color: "#666" }}
                              />
                          }
                        })()}

                        <Typography
                          variant="content"
                          onClick={() => !disabled && previewAvailable && openPreview(index)}
                          sx={{
                            cursor: (previewAvailable && !disabled) ? "pointer" : "unset",
                            textDecoration: previewAvailable ? "underline" : "none",
                            wordBreak: "break-all",
                          }}>
                          {fileDetail.name ?? getDefaultname()}
                        </Typography>

                        <div style={{ flexGrow: 1 }} />

                        {!readOnly && <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => {
                            onFileChange([...value.filter((_, i) => i !== index)], onChange);
                          }}
                        >
                          <ClearIcon
                            sx={{ fontSize: "18px", color: "#666" }}
                          />
                        </IconButton>}
                      </ Stack>
                    )
                  })
                }
                {!noErrorMessage && error && <Typography
                  variant="content"
                  sx={{ color: c["danger"] }}
                >{error.message}</Typography>}
              </Stack>
              {canPreview && value.map((fileDetail, index) => {
                return <Backdrop
                  key={(fileDetail.name ?? "").concat(fileDetail.content.slice(0, 20))} //unique key to retain dom child
                  open={previewOpen && previewIndex === index}
                  onClick={() => {
                    setPreviewOpen(false);
                    setPreviewIndex(null);
                  }}
                  sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                >
                  <Paper
                    sx={{
                      width: "50%",
                      height: "80%",
                    }}>
                    {(() => {
                      switch (fileDetail.type) {
                        case "pdf":
                          return <object
                            data={fileDetail.content}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                          >
                            <p>
                              Bro dont support pdf
                            </p>
                          </object>
                        case "httpLink":
                          return <iframe
                            src={fileDetail.content}
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                          />
                        case "any":
                          return <Typography variant="content">
                            {fileDetail.content}
                          </Typography>
                      }
                    })()}
                  </Paper>
                </Backdrop>
              })}
            </>
          )
        }}
    />
  )
}

export default FormBuilderFilePicker